/** Dropdown menu with keyboard navigation. Wraps React Aria Menu. */
import {
  Menu as AriaMenu,
  MenuTrigger as AriaMenuTrigger,
  MenuItem as AriaMenuItem,
  MenuSection as AriaMenuSection,
  SubmenuTrigger as AriaSubmenuTrigger,
  Header,
  Separator,
  Keyboard,
  type MenuProps as AriaMenuProps,
  type MenuTriggerProps as AriaMenuTriggerProps,
  type MenuItemProps as AriaMenuItemProps,
  type SectionProps as AriaSectionProps,
} from 'react-aria-components';
import { cn } from '@/foundation/utils/cn';

interface MenuProps<T extends object> extends AriaMenuProps<T> {
  className?: string;
}

export function Menu<T extends object>({ className, ...props }: MenuProps<T>) {
  return <AriaMenu className={cn('outline-none p-1', className)} {...props} />;
}

export function MenuTrigger(props: AriaMenuTriggerProps) {
  return <AriaMenuTrigger {...props} />;
}

interface MenuItemProps extends AriaMenuItemProps {
  className?: string;
  isDestructive?: boolean;
  shortcut?: string;
}

export function MenuItem({
  className,
  isDestructive,
  shortcut,
  children,
  ...props
}: MenuItemProps) {
  return (
    <AriaMenuItem
      className={cn(
        'flex items-center gap-2 px-3 py-2 rounded-lg cursor-pointer outline-none text-sm transition-colors',
        isDestructive
          ? 'text-[var(--f-error-text)] data-[focused]:bg-[var(--f-error-subtle)]'
          : 'text-[var(--f-fg-secondary)] data-[focused]:bg-[var(--f-bg-sunken)]',
        'data-[disabled]:opacity-50 data-[disabled]:cursor-not-allowed',
        className
      )}
      {...props}
    >
      {(renderProps) => (
        <>
          <span className="flex-1 flex items-center gap-2">
            {typeof children === 'function' ? children(renderProps) : children}
          </span>
          {shortcut && (
            <Keyboard className="text-xs text-[var(--f-fg-muted)] ml-auto">{shortcut}</Keyboard>
          )}
        </>
      )}
    </AriaMenuItem>
  );
}

interface MenuSectionProps<T extends object> extends Omit<AriaSectionProps<T>, 'children'> {
  title?: string;
  className?: string;
  children?: React.ReactNode;
}

export function MenuSection<T extends object>({
  title,
  className,
  children,
  ...props
}: MenuSectionProps<T>) {
  return (
    <AriaMenuSection className={className} {...props}>
      {title && (
        <Header className="text-xs font-semibold uppercase tracking-wider px-3 py-1.5 text-[var(--f-fg-muted)]">
          {title}
        </Header>
      )}
      {children}
      <Separator className="my-1 h-px bg-[var(--f-border)]" />
    </AriaMenuSection>
  );
}

export { AriaSubmenuTrigger as SubmenuTrigger };
